import { useMemo, useState } from 'react';
import { Image, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import {
  BasicModal,
  Button,
  CustomIcon,
  FormModal,
  InputSelect,
  ReleasesModal,
  RestrictedComponent
} from '~components/index';
import { INPUT_REQUIRED, PERMISSIONS } from '~constants/index';
import { useLocalStorage } from '~hooks/index';
import { logout } from '~layouts/ErrorBoundaries/ErrorBoundaryBack/ErrorBoundaryBackRequests';
import {
  getUserValidationTenant,
  type Tenant
} from '~pages/Public/LoginPage/LoginRequests';
import { ROUTES } from '~routes/index';
import { useAppStorage } from '~store/useStore';
import { sidebarItemsRender } from './SidebarHandler';
import { createCode, updateUserStorage } from './SidebarRequests';
import './sidebar.scss';

const defaultLanguage = import.meta.env.VITE_DEFAULT_LANGUAGE;

interface Props {
  collapse: boolean;
}
const Sidebar = ({ collapse }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { appName, userData, appImplementation, userPermissions, setLanguage } =
    useLocalStorage();
  const { isDoingRequest, doingRequest, requestFinalized } = useAppStorage();
  const [showModal, setShowModal] = useState('none');
  const [code, setCode] = useState<string | null>(null);
  const tenantOptions = useMemo(
    () =>
      (userData?.availableTenants ?? [])
        .filter((tenant) => tenant.name !== 'default')
        .map((tenant) => ({ label: tenant.name, value: tenant })),
    [userData?.availableTenants]
  );

  const viewOperation = [
    {
      title: t('labels.operation_cuc'),
      icon: 'journalMedical',
      isVisible: userPermissions.includes(
        PERMISSIONS.PATIENT_MANAGEMENT.CAN_VIEW_OPERATION_CUC
      ),
      tenant: 'cuc'
    },
    {
      title: t('labels.operation_csb'),
      icon: 'journalMedical',
      isVisible: userPermissions.includes(
        PERMISSIONS.PATIENT_MANAGEMENT.CAN_VIEW_OPERATION_CSB
      ),
      tenant: 'csb'
    },
    {
      title: t('labels.operation_cec'),
      icon: 'journalMedical',
      isVisible: userPermissions.includes(
        PERMISSIONS.PATIENT_MANAGEMENT.CAN_VIEW_OPERATION_CC
      ),
      tenant: 'cc'
    },
    {
      title: t('labels.operation_iberoamerican'),
      icon: 'journalMedical',
      isVisible: userPermissions.includes(
        PERMISSIONS.PATIENT_MANAGEMENT.CAN_VIEW_OPERATION_IBEROAMERICAN
      ),
      tenant: 'iberoamerica'
    }
  ].filter((operation) => operation.isVisible);

  const clearAppStorage = () => {
    localStorage.removeItem(appName);
    window.location.replace('/');
    setLanguage(defaultLanguage);
  };

  const availableModals: Record<string, JSX.Element> = {
    deployModal:( <ReleasesModal onHide={() => setShowModal('none')} />),
    generateCode: (
      <FormModal
        title="btns.generate_code"
        inputs={() => [
          {
            id: 'document_number_id',
            type: 'text',
            name: 'document_number',
            label: 'labels.document_number',
            placeholder: 'placeholders.document_number',
            rules: INPUT_REQUIRED
          }
        ]}
        submitFn={(data) => {
          const { document_number } = data as { document_number: string };
          if (document_number) createCode({ document_number, setCode });
        }}
        extraBody={
          code && (
            <div className="d-flex gap-1">
              <p className="fw-bold">{t('labels.code_generated')}</p>
              {code}
            </div>
          )
        }
        onHide={() => {
          setShowModal('none');
          setCode(null);
        }}
      />
    ),
    tenantModal: (
      <BasicModal
        title=""
        body={
          <div className="px-3 d-flex flex-column align-items-center text-center">
            <CustomIcon
              name="hospital"
              size="LG"
              color="var(--app-primary-color)"
            />
            <div className="w-75 m-auto mb-3">
              <p
                className="fw-bold"
                style={{ fontSize: 20, color: 'var(--app-primary-color)' }}
              >
                {t('confirmation.select_clinic')}
              </p>
              <p>{t('confirmation.select_clinic_extra_desc')}</p>
            </div>
            <InputSelect
              name="tenant"
              options={tenantOptions}
              placeholder="placeholders.select_tenant"
              onEventChange={async (tenant) => {
                doingRequest();
                const tenantSelected = tenant as SelectData<Tenant>;
                await getUserValidationTenant(
                  userData?.userCredentials,
                  tenantSelected.value,
                  () => {
                    updateUserStorage();
                    navigate(ROUTES.PLATFORM);
                  }
                );
              }}
              disabled={isDoingRequest}
            />
            {isDoingRequest && (
              <div className="my-1 d-flex flex-column align-items-center justify-content-center">
                <Spinner role="loading_spinner" animation="border" />
                <p className="mt-2 text-wrap">{t('labels.retrieving_user')}</p>
              </div>
            )}
          </div>
        }
        onHide={() => {
          setShowModal('none');
          requestFinalized();
        }}
      />
    )
  };

  const viewUserProfile = userPermissions.includes(
    PERMISSIONS.USERS.CAN_VIEW_USER_PROFILE
  );

  return (
    <>
      {showModal !== 'none' && availableModals[showModal]}
      <div
        className={`sidebar ${collapse ? 'sidebar_hidden' : 'sidebar_visible'}`}
      >
        <div className="sidebar_content">
          <NavLink to={ROUTES.PLATFORM} className="sidebar_logo_container">
            <Image
              src={appImplementation?.logos?.logo_inverse}
              alt="app_logo"
              className="sidebar_logo"
              width={130}
            />
          </NavLink>
          <RestrictedComponent
            permissions={[PERMISSIONS.NAV_BAR.CAN_VIEW_TENANT_NAME]}
          >
            <p className="sidebar_tenant_name mb-2">
              {userData?.tenantSelected?.name}
            </p>
          </RestrictedComponent>
          <div className="d-flex flex-row gap-2">
            {userData.is_superuser && (
              <Button
                variant="link"
                iconName="releases"
                title="btns.releases"
                onClick={() => setShowModal('deployModal')}
              />
            )}

            {tenantOptions.length > 1 && (
              <Button
                variant="link"
                iconName="uiChecks"
                onClick={() => setShowModal('tenantModal')}
              />
            )}
          </div>
          {sidebarItemsRender(
            userData,
            location,
            viewOperation.length > 0 ? viewOperation[0] : null
          )}
          <RestrictedComponent
            permissions={[PERMISSIONS.NAV_BAR.CAN_VIEW_GENERATE_CODE]}
          >
            <Button
              variant="link"
              text="btns.generate_code"
              iconName="outlineSecurity"
              onClick={() => setShowModal('generateCode')}
            />
          </RestrictedComponent>
          <div className="sidebar_userOptions">
            <div
              className={viewUserProfile ? 'sidebar_userProfile' : ''}
              title={t('btns.user_profile')}
              onClick={
                viewUserProfile
                  ? () => navigate(ROUTES.USER_PROFILE)
                  : undefined
              }
            >
              <CustomIcon
                name="user"
                color="var(--sidebar-icon-color)"
                size="LG"
                className={viewUserProfile ? 'cursor-pointer' : ''}
              />
            </div>

            <div className="sidebar_userLogged">
              <div className="d-flex gap-2 align-items-center">
                {userData?.first_name ?? ' - '}
                <div title={t('btns.update_user')} onClick={updateUserStorage}>
                  <CustomIcon
                    name="reload"
                    color="var(--sidebar-text-color)"
                    size="XS"
                    className="cursor-pointer"
                  />
                </div>
              </div>
              <Button
                variant="link"
                iconName="logout"
                text="btns.logout"
                onClick={() =>
                  logout(userData?.tokens?.refresh, clearAppStorage)
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
